import * as React from "react";
import Accordion from "src/Components/Accordion";
import { TableLoadingFrame } from "src/Components/Loading/TableSkeleton";
import Pagination from "src/Components/Pagination";
import {
  ExtraType,
  Header,
  IAddNewData,
  ISearchParameters,
  ITableReport,
  Report,
} from "src/Interfaces/TableInterfaces";
import { toast } from "react-toastify";
import StaticDropdown from "src/Components/Dropdown/StaticDropdown";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { AxiosRequestConfig } from "axios";

export interface IAdditionalRowProps<
  RowType extends ITableReport,
> extends IAdditionalHeaderProps {
  row: RowType;
}

export interface IAdditionalHeaderProps {
  onAction: () => void;
  selectedRowsIds: number[];
  setSelectedRowsIds: (selectedRowsIds: number[]) => void;
  isSelectAll: boolean;
}

export interface IAdditional<RowType extends ITableReport> {
  getHeader: React.FC<IAdditionalHeaderProps>;
  getRow: React.FC<{
    row: RowType;
    onAction: () => void;
    selectedRowsIds: number[];
    setSelectedRowsIds: (selectedRowsIds: number[]) => void;
  }>;
}

export interface IFilterTableCols {
  show: boolean;
  tableName: string;
  allowedPositions: string[];
}

export interface ITableUI<ReportType extends ITableReport> {
  tableBoxClassName?: string;
  tableHeaderClassName?: string;
  reports: Report<ReportType>;
  AdditionalBody?: IAdditional<ReportType> | null;
  RightSideAdditionalBody?: IAdditional<ReportType> | null;
  onAction?: () => void;
  withSelect?: boolean;
  onTableRowClick?: (row: ReportType) => void;
  onTableHeaderClick?: (header: Header) => void;
  onBulkCancelClick?: (selectedRowsIds: number[]) => void;
  sortingType?: string;
  setSortingType?: (sortingType: string) => void;
  showHiddenCols?: boolean;
  getExtraTypeUI?: (values: IExtraTypeUIProps<ReportType>) => JSX.Element;
  hideWhenNoResp?: boolean;
  refreshFlag?: boolean;
  activeRowId?: number;
  onSelectedRowsChange?: (activeRowId: number[]) => void;
  ref?: React.MutableRefObject<any>;
  scrollKey?: string;
  filterCols?: IFilterTableCols;
  type?: "aggregate" | "portfo";
}

export interface IAddNewDataUI<ValueType> extends IAddNewData {
  onSubmit: () => void;
  onChange: (name: string, value: any) => void;
  values: ValueType;
}

export interface ITable<
  SearchParam extends ISearchParameters,
  ReportType extends ITableReport,
> {
  initialSearchParameters?: SearchParam;
  searchParameters?: SearchParam;
  getDataTable: (
    searchParameters: SearchParam,
    offset: number | null,
    pageSize: number | null,
    sortingType?: string,
    additional?: DropdownItem,
    axiosOptions?: AxiosRequestConfig,
  ) => Promise<Report<ReportType>>;

  TableUI: React.FC<ITableUI<ReportType>>;
  SearchParamUI?: (props: ISearchparamUI<SearchParam>) => any;
  refreshTableTime?: number;
  showIsLoading?: boolean;
  pagesize?: number;
  withSort?: boolean;
  getExtraTypeUI?: (values: IExtraTypeUIProps<ReportType>) => JSX.Element;
  showPageSize?: boolean;
  tableWrapperClassname?: string;
  onFetchComplete?: (pageNumber?: number) => void;
  ref?: React.MutableRefObject<any>;
}

const TableTemplate = <
  SearchParam extends ISearchParameters,
  ReportType extends ITableReport,
>({
  getDataTable,
  initialSearchParameters,
  TableUI,
  SearchParamUI,
  refreshTableTime,
  showIsLoading = true,
  pagesize = 50,
  searchParameters,
  withSort,
  getExtraTypeUI,
  showPageSize,
  tableWrapperClassname,
  onFetchComplete,
}: ITable<SearchParam, ReportType>) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [response, setResponse] = React.useState<Report<ReportType>>();
  const [searchParams, setSearchParams] = React.useState<SearchParam>(
    initialSearchParameters || searchParameters || ({} as SearchParam),
  );
  const latestRequestTime = React.useRef<number | null>(null); // Keeps track of the latest request time
  const [scrollKey] = React.useState(() =>
    crypto.randomUUID
      ? crypto.randomUUID()
      : `scroll-${Date.now()}-${Math.random()}`,
  );
  const [sortingType, setSortingType] = React.useState("");
  const [pageSize, setPageSize] = React.useState(pagesize);

  const abortControllerRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => {
    if (searchParameters) {
      setSearchParams(searchParameters);
      setOffset(0);
    }
  }, [searchParameters]);

  const [offset, setOffset] = React.useState<number>(0);

  React.useEffect(() => {
    reload(true);
  }, [searchParams, offset, sortingType, pageSize]);

  React.useEffect(() => {
    if (
      response &&
      response.translatedHeaders &&
      response.translatedHeaders.length
    )
      if (refreshTableTime) {
        const timeoutId = setTimeout(() => {
          reload(false, true);
        }, refreshTableTime);

        return () => clearTimeout(timeoutId);
      }
  }, [response]);

  React.useEffect(() => {
    return () => {
      sessionStorage.removeItem(scrollKey);
    };
  }, []);

  const fetchData = (
    offset: number,
    newSearchParam: SearchParam,
    showIsLoading = true,
    isAutoRefresh = false,
  ) => {
    if (showIsLoading) setIsLoading(true);

    // Abort previous request if it’s still pending
    if (abortControllerRef.current) {
      if (isAutoRefresh) return;
      abortControllerRef.current.abort();
    }

    // Create a new controller for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    getDataTable(newSearchParam, offset, pageSize, sortingType, undefined, {
      signal: controller.signal,
    })
      .then((res: Report<ReportType>) => {
        setResponse(() => ({
          ...res,
          requestDate: new Date().toISOString(),
        }));
        if (showIsLoading) setIsLoading(false);
      })
      .catch((e) => {
        if (e.name === "CanceledError" || e.name === "AbortError") return;
        else {
          if (e.response?.data?.value) toast.error(e.response.data.value);
          if (e.response?.status === 500) toast.error("خطا داخلی");
          else toast.error("خطا در دریافت اطلاعات");
          if (showIsLoading) setIsLoading(false);
        }
      })
      .finally(() => {
        abortControllerRef.current = null;
        // if (showIsLoading) setIsLoading(false);  // don't remove the comment causes race condition setting isLoading to false when request was canceled by new request :)
        if (onFetchComplete) {
          onFetchComplete(Math.ceil((offset + 1) / pageSize));
        }
        // if (!isAutoRefresh) latestRequestTime.current = null;
      });
  };

  const onPageChange = (e: any, p: number) => {
    const ps = pageSize;
    const off = (e - 1) * ps;
    setOffset(off);
    fetchData(off, searchParams);
  };

  const searchParamSubmitHandler = (newSearchParam: SearchParam) => {
    setSearchParams(newSearchParam);
    setOffset(0);
    fetchData(0, newSearchParam);
  };

  const reload = (isLoading = false, isAutoRefresh = false) => {
    fetchData(offset, searchParams, isLoading, isAutoRefresh);
  };

  const sortingTypeSetter = (headerName: string) => {
    if (sortingType === `${headerName}_asc`)
      setSortingType(`${headerName}_desc`);
    else setSortingType(`${headerName}_asc`);
  };

  return (
    <div
      className={`max-w-full mx-auto flex flex-row justify-center items-start gap-3 ${tableWrapperClassname}`}
    >
      {SearchParamUI ? (
        <Accordion
          className="mb-2"
          title="مقادیر جستجو"
          content={
            <SearchParamUI
              intialValue={initialSearchParameters || ({} as SearchParam)}
              onSubmit={searchParamSubmitHandler}
              key={122}
            />
          }
        />
      ) : null}
      {/* <div className="max-w-min mx-auto mb-2"> */}
      <TableBody<ReportType>
        isLoading={isLoading}
        TableUI={TableUI}
        offset={offset}
        onPageChange={onPageChange}
        reload={reload}
        response={response}
        setSortingType={withSort ? sortingTypeSetter : undefined}
        sortingType={withSort ? sortingType : undefined}
        getExtraTypeUI={getExtraTypeUI}
        pageSize={pageSize}
        setPageSize={setPageSize}
        showPageSize={showPageSize}
        scrollKey={scrollKey}
      />
      {/* </div> */}
    </div>
  );
};

const TableBody = <ReportType extends ITableReport>({
  isLoading,
  response,
  offset,
  reload,
  TableUI,
  onPageChange,
  setSortingType,
  sortingType,
  getExtraTypeUI,
  pageSize,
  setPageSize,
  showPageSize,
  scrollKey = "#",
}: ITableBody<ReportType>) => {
  const pageSizeOptions: DropdownItem[] = [
    { id: 50, name: "50" },
    { id: 100, name: "100" },
    { id: 500, name: "500" },
    { id: 1000, name: "1000" },
  ];

  const hasTable = Boolean(
    response && response.translatedHeaders && response.report,
  );

  if (!isLoading && !hasTable) {
    return (
      <div className="m-auto max-w-min whitespace-nowrap mt-10">
        <div>متاسفانه داده ای یافت نشد</div>
      </div>
    );
  }

  return (
    <TableLoadingFrame
      isLoading={isLoading}
      columns={response?.translatedHeaders?.length}
      rows={response?.report?.length}
      table={
        hasTable && response ? (
          <TableUI
            reports={response}
            onAction={reload}
            sortingType={sortingType}
            setSortingType={setSortingType}
            getExtraTypeUI={getExtraTypeUI}
            scrollKey={scrollKey}
          />
        ) : null
      }
      footer={
        hasTable ? (
          <div
            className={`flex flex-row ${
              showPageSize ? "justify-between" : "justify-center"
            } items-center w-full`}
          >
            {showPageSize ? <div /> : <></>}
            <Pagination
              count={response?.count}
              offset={response?.offset}
              pageSize={response?.pageSize}
              onChange={onPageChange}
            />
            {showPageSize && response && response.count ? (
              <div className="w-40">
                <StaticDropdown
                  dropdown={{
                    name: "",
                    placeholder: "تعداد نمایش",
                    extraLabel: true,
                  }}
                  onChange={(_, v) => setPageSize(v)}
                  options={pageSizeOptions}
                  value={pageSize}
                />
              </div>
            ) : (
              <></>
            )}
          </div>
        ) : null
      }
    />
  );
};

export interface IExtraTypeUIProps<T extends ITableReport> {
  row: T;
  extraType?: ExtraType;
  headerName: string;
  cellType: string;
}

export interface ITableBody<ReportType extends ITableReport> {
  isLoading: boolean;
  response?: Report<ReportType>;
  offset: number;
  TableUI: React.FC<ITableUI<ReportType>>;
  reload: () => void;
  onPageChange: (e: any, p: number) => void;
  sortingType?: string;
  setSortingType?: (sortingType: string) => void;
  getExtraTypeUI?: (values: IExtraTypeUIProps<ReportType>) => JSX.Element;
  pageSize: number;
  setPageSize: (ps: number) => void;
  showPageSize?: boolean;
  scrollKey?: string;
}

export interface ISearchparamUI<SearchParam extends ISearchParameters> {
  intialValue: SearchParam;
  onSubmit: (searchparm: SearchParam) => void;
}

export interface IFormSearchParam<SearchParam extends ISearchParameters> {
  onChange: (name: string, value: any) => void;
  searchParam: SearchParam;
  onSubmit: () => void;
  children?: React.ReactElement;
}
export interface ISearchparamUIWithHandler<
  SearchParam extends ISearchParameters,
> extends ISearchparamUI<SearchParam> {
  Form: (props: IFormSearchParam<SearchParam>) => any;
  autoSubmit?: boolean;
}

export const SearchparamStateHandler = <SearchParam extends ISearchParameters>({
  intialValue,
  onSubmit,
  Form,
  autoSubmit = false,
}: ISearchparamUIWithHandler<SearchParam>) => {
  const [values, setValues] = React.useState<SearchParam>(intialValue);

  const onFormSubmit = () => {
    onSubmit(values);
  };
  const onFormOnChange = (name: string, v: any) => {
    if (name) setValues({ ...values, [name]: v });
    else setValues(v);

    if (autoSubmit) {
      if (name) onSubmit({ ...values, [name]: v });
      else onSubmit(v);
    }
  };

  return (
    <Form
      onSubmit={onFormSubmit}
      onChange={onFormOnChange}
      searchParam={values}
    />
  );
};

export default TableTemplate;
